import { EFinancingProgram } from '@if/core/types'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '@src/services/api-client'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import {
  B2cWorksheet,
  B2cWorksheetDecisionDTO,
  B2cWorksheetRevision,
  EditB2cWorksheetDto,
} from '../../types/B2cWorksheetSchema'
import { FundingComputedDto } from '../../types/FundingComputedDto'
import { FundingInfoDto } from '../../types/FundingInfoDto'
import { FullCreditApplication } from '../../types'

const baseUrl = '/Applications/b2c/{creditApplicationId}/Worksheet'

// Query Keys
export const b2cWorksheetKeys = {
  all: ['b2cWorksheet'] as const,
  revisions: (creditApplicationId: string) => [...b2cWorksheetKeys.all, 'revisions', creditApplicationId] as const,
}

// API Functions
export async function createB2cWorksheet(data: EditB2cWorksheetDto): Promise<B2cWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.post<B2cWorksheet>(url, data)
  return response.data
}

export async function updateB2cWorksheet(data: EditB2cWorksheetDto): Promise<B2cWorksheet> {
  const apiClient = getApiClient()
  const url = baseUrl.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<B2cWorksheet>(url, data)
  return response.data
}

export async function computeB2cFunding(data: FundingInfoDto): Promise<FundingComputedDto> {
  const apiClient = getApiClient()
  const response = await apiClient.post<FundingComputedDto>('Applications/ComputeFunding', data)
  return response.data
}

export async function updateB2cWorksheetDecision(data: B2cWorksheetDecisionDTO): Promise<B2cWorksheet> {
  const apiClient = getApiClient()
  const url = `${baseUrl}/Decision`.replace('{creditApplicationId}', data.creditApplicationId!)
  const response = await apiClient.put<B2cWorksheet>(url, data)
  return response.data
}

export async function getB2cWorksheetRevisions(creditApplicationId: string): Promise<B2cWorksheetRevision[]> {
  const apiClient = getApiClient()
  const url = `${baseUrl}/Revisions`.replace('{creditApplicationId}', creditApplicationId)
  const response = await apiClient.get<B2cWorksheetRevision[]>(url)
  return response.data
}

// React-Query Hooks
export function useCreateB2cWorksheet(): [MutationFunction<B2cWorksheet, EditB2cWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: createB2cWorksheet,
    onSuccess: (createdWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(createdWorksheet.creditApplicationId, EFinancingProgram.B2c)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: createdWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useUpdateB2cWorksheet(): [MutationFunction<B2cWorksheet, EditB2cWorksheetDto>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateB2cWorksheet,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.B2c)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useComputeB2cFunding(): [MutationFunction<FundingComputedDto, FundingInfoDto>, boolean] {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: computeB2cFunding,
  })

  return [mutateAsync, isPending]
}

export function useUpdateB2cWorksheetDecision(): [MutationFunction<B2cWorksheet, B2cWorksheetDecisionDTO>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateB2cWorksheetDecision,
    onSuccess: (updatedWorksheet) => {
      const queryKey = fullCreditApplicationKeys.detail(updatedWorksheet.creditApplicationId, EFinancingProgram.B2c)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          worksheet: updatedWorksheet,
        })
      }
    },
  })

  return [mutateAsync, isPending]
}

export function useGetB2cWorksheetRevisions(creditApplicationId: string | undefined): [B2cWorksheetRevision[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: b2cWorksheetKeys.revisions(creditApplicationId ?? ''),
    queryFn: () => getB2cWorksheetRevisions(creditApplicationId!),
    enabled: !!creditApplicationId,
  })
  return [data ?? [], isFetching]
}
